import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Storage as ServerIcon,
  PhoneInTalk as VicidialIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://102.220.100.99/api';

const Tenants = () => {
  const { token } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openServerDialog, setOpenServerDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedServer, setSelectedServer] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    description: '',
    primaryColor: '#2196f3'
  });
  const [serverFormData, setServerFormData] = useState({
    name: '',
    host: '',
    port: 3306,
    database: 'asterisk',
    username: '',
    password: '',
    internal_ip: '',
    use_internal_network: false,
    direct_file_access: false,
    file_transfer_method: 'none',
    ssh_user: '',
    ssh_password: '',
    ssh_port: 22,
    recordings_path: '/var/spool/asterisk/monitor',
    api_key: '',
    tenant_id: null
  });

  const fetchTenants = async () => {
    try {
      const response = await fetch(`${API_URL}/tenants`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch tenants');
      }

      setTenants(data.data || data);
    } catch (err) {
      console.error('Fetch tenants error:', err);
      setError(err.message || 'Failed to fetch tenants');
    } finally {
      setLoading(false);
    }
  };

  const fetchServers = async () => {
    try {
      const response = await fetch(`${API_URL}/servers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch servers');
      }

      setServers(data.data || data);
    } catch (err) {
      console.error('Fetch servers error:', err);
      setError(err.message || 'Failed to fetch servers');
    }
  };

  useEffect(() => {
    fetchTenants();
    fetchServers();
  }, []);

  const handleOpenDialog = (tenant = null) => {
    if (tenant) {
      setFormData({
        name: tenant.name,
        domain: tenant.domain,
        description: tenant.description || '',
        primaryColor: tenant.primary_color
      });
      setSelectedTenant(tenant);
    } else {
      setFormData({
        name: '',
        domain: '',
        description: '',
        primaryColor: '#2196f3'
      });
      setSelectedTenant(null);
    }
    setOpenDialog(true);
  };

  const handleOpenServerDialog = (server = null, tenantId = null) => {
    if (server) {
      setServerFormData({
        name: server.name,
        host: server.host,
        port: server.port,
        database: server.database,
        username: server.username,
        password: server.password,
        internal_ip: server.internal_ip || '',
        use_internal_network: server.use_internal_network,
        direct_file_access: server.direct_file_access,
        file_transfer_method: server.file_transfer_method,
        ssh_user: server.ssh_user || '',
        ssh_password: server.ssh_password || '',
        ssh_port: server.ssh_port,
        recordings_path: server.recordings_path,
        api_key: server.api_key || '',
        tenant_id: server.tenant_id
      });
      setSelectedServer(server);
    } else {
      setServerFormData({
        name: '',
        host: '',
        port: 3306,
        database: 'asterisk',
        username: '',
        password: '',
        internal_ip: '',
        use_internal_network: false,
        direct_file_access: false,
        file_transfer_method: 'none',
        ssh_user: '',
        ssh_password: '',
        ssh_port: 22,
        recordings_path: '/var/spool/asterisk/monitor',
        api_key: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
        tenant_id: tenantId
      });
      setSelectedServer(null);
    }
    setOpenServerDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTenant(null);
    setFormData({
      name: '',
      domain: '',
      description: '',
      primaryColor: '#2196f3'
    });
  };

  const handleCloseServerDialog = () => {
    setOpenServerDialog(false);
    setSelectedServer(null);
    setServerFormData({
      name: '',
      host: '',
      port: 3306,
      database: 'asterisk',
      username: '',
      password: '',
      internal_ip: '',
      use_internal_network: false,
      direct_file_access: false,
      file_transfer_method: 'none',
      ssh_user: '',
      ssh_password: '',
      ssh_port: 22,
      recordings_path: '/var/spool/asterisk/monitor',
      api_key: '',
      tenant_id: null
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = selectedTenant
        ? `${API_URL}/tenants/${selectedTenant.id}`
        : `${API_URL}/tenants`;
      
      const response = await fetch(url, {
        method: selectedTenant ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Failed to ${selectedTenant ? 'update' : 'create'} tenant`);
      }

      await fetchTenants();
      handleCloseDialog();
    } catch (err) {
      console.error('Submit tenant error:', err);
      setError(err.message || `Failed to ${selectedTenant ? 'update' : 'create'} tenant`);
    }
  };

  const handleServerSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = selectedServer
        ? `${API_URL}/servers/${selectedServer.id}`
        : `${API_URL}/servers`;
      
      const response = await fetch(url, {
        method: selectedServer ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(serverFormData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Failed to ${selectedServer ? 'update' : 'create'} server`);
      }

      await fetchServers();
      handleCloseServerDialog();
    } catch (err) {
      console.error('Submit server error:', err);
      setError(err.message || `Failed to ${selectedServer ? 'update' : 'create'} server`);
    }
  };

  const handleDelete = async (tenant) => {
    if (!window.confirm(`Are you sure you want to delete ${tenant.name}?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/tenants/${tenant.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete tenant');
      }

      await fetchTenants();
    } catch (err) {
      console.error('Delete tenant error:', err);
      setError(err.message || 'Failed to delete tenant');
    }
  };

  const handleDeleteServer = async (server) => {
    if (!window.confirm(`Are you sure you want to delete server ${server.name}?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/servers/${server.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete server');
      }

      await fetchServers();
    } catch (err) {
      console.error('Delete server error:', err);
      setError(err.message || 'Failed to delete server');
    }
  };

  const getServersByTenant = (tenantId) => {
    return servers.filter(server => server.tenant_id === tenantId);
  };

  const generateWebhookUrl = (serverId, apiKey) => {
    const baseUrl = window.location.origin;
    return {
      callComplete: `${baseUrl}/webhook/vicidial/call-complete`,
      recordingComplete: `${baseUrl}/webhook/vicidial/recording-complete`
    };
  };

  const copyToClipboard = (text) => {
    const input = document.createElement('input');
    input.value = text;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
  };

  if (loading) {
    return (
      <Box p={3}>
        <Typography>Loading tenants...</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h1">
          Tenant Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Tenant
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tenants List with Servers */}
      {tenants.map((tenant) => {
        const tenantServers = getServersByTenant(tenant.id);
        return (
          <Accordion key={tenant.id} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Box>
                  <Typography variant="h6">{tenant.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {tenant.domain} • {tenantServers.length} server(s)
                  </Typography>
                </Box>
                <Box>
                  <Chip 
                    label={tenant.active ? 'Active' : 'Inactive'} 
                    color={tenant.active ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Tenant Details
                      </Typography>
                      <Typography><strong>Name:</strong> {tenant.name}</Typography>
                      <Typography><strong>Domain:</strong> {tenant.domain}</Typography>
                      <Typography><strong>Description:</strong> {tenant.description || 'No description'}</Typography>
                      <Box mt={2}>
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenDialog(tenant)}
                          sx={{ mr: 1 }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDelete(tenant)}
                        >
                          Delete
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                          <VicidialIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Vicidial Servers
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenServerDialog(null, tenant.id)}
                        >
                          Add Server
                        </Button>
                      </Box>
                      {tenantServers.length === 0 ? (
                        <Typography color="textSecondary">No servers configured</Typography>
                      ) : (
                        tenantServers.map((server) => (
                          <Box key={server.id} mb={2} p={2} border={1} borderColor="grey.300" borderRadius={1}>
                            <Typography><strong>{server.name}</strong></Typography>
                            <Typography variant="body2">Host: {server.host}:{server.port}</Typography>
                            <Typography variant="body2">Database: {server.database}</Typography>
                            <Typography variant="body2">
                              Status: <Chip 
                                label={server.active ? 'Active' : 'Inactive'} 
                                color={server.active ? 'success' : 'default'}
                                size="small"
                              />
                            </Typography>
                            <Box mt={1}>
                              <Button
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => handleOpenServerDialog(server)}
                                sx={{ mr: 1 }}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => handleDeleteServer(server)}
                              >
                                Delete
                              </Button>
                            </Box>
                          </Box>
                        ))
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Tenant Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {selectedTenant ? 'Edit Tenant' : 'Add New Tenant'}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Tenant Name"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              margin="dense"
              label="Domain"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              required
              helperText="e.g., mycompany.qualitron.ai"
            />
            <TextField
              margin="dense"
              label="Description"
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Primary Color"
              type="color"
              fullWidth
              variant="outlined"
              value={formData.primaryColor}
              onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {selectedTenant ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Server Dialog */}
      <Dialog open={openServerDialog} onClose={handleCloseServerDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleServerSubmit}>
          <DialogTitle>
            {selectedServer ? 'Edit Vicidial Server' : 'Add New Vicidial Server'}
          </DialogTitle>
          <DialogContent>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
              <Tab label="Basic Settings" />
              <Tab label="Database" />
              <Tab label="File Transfer" />
              <Tab label="Webhooks" />
            </Tabs>
            
            {/* Basic Settings Tab */}
            {tabValue === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="Server Name"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={serverFormData.name}
                    onChange={(e) => setServerFormData({ ...serverFormData, name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    margin="dense"
                    label="Host"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={serverFormData.host}
                    onChange={(e) => setServerFormData({ ...serverFormData, host: e.target.value })}
                    required
                    helperText="IP address or hostname of your Vicidial server"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    margin="dense"
                    label="Port"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={serverFormData.port}
                    onChange={(e) => setServerFormData({ ...serverFormData, port: parseInt(e.target.value) })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Internal IP (Optional)"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={serverFormData.internal_ip}
                    onChange={(e) => setServerFormData({ ...serverFormData, internal_ip: e.target.value })}
                    helperText="Internal network IP for faster file transfers"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={serverFormData.use_internal_network}
                        onChange={(e) => setServerFormData({ ...serverFormData, use_internal_network: e.target.checked })}
                      />
                    }
                    label="Use Internal Network"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={serverFormData.direct_file_access}
                        onChange={(e) => setServerFormData({ ...serverFormData, direct_file_access: e.target.checked })}
                      />
                    }
                    label="Direct File Access"
                  />
                </Grid>
              </Grid>
            )}
            
            {/* Database Tab */}
            {tabValue === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Database Name"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={serverFormData.database}
                    onChange={(e) => setServerFormData({ ...serverFormData, database: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    label="Username"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={serverFormData.username}
                    onChange={(e) => setServerFormData({ ...serverFormData, username: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    label="Password"
                    type="password"
                    fullWidth
                    variant="outlined"
                    value={serverFormData.password}
                    onChange={(e) => setServerFormData({ ...serverFormData, password: e.target.value })}
                    required
                  />
                </Grid>
              </Grid>
            )}
            
            {/* File Transfer Tab */}
            {tabValue === 2 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth margin="dense">
                    <InputLabel>File Transfer Method</InputLabel>
                    <Select
                      value={serverFormData.file_transfer_method}
                      onChange={(e) => setServerFormData({ ...serverFormData, file_transfer_method: e.target.value })}
                      label="File Transfer Method"
                    >
                      <MenuItem value="none">None (No file transfer)</MenuItem>
                      <MenuItem value="direct">Direct Access (Shared storage)</MenuItem>
                      <MenuItem value="rsync">Rsync</MenuItem>
                      <MenuItem value="sftp">SFTP</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Recordings Path"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={serverFormData.recordings_path}
                    onChange={(e) => setServerFormData({ ...serverFormData, recordings_path: e.target.value })}
                    helperText="Path to recordings directory on Vicidial server"
                  />
                </Grid>
                {(serverFormData.file_transfer_method === 'rsync' || serverFormData.file_transfer_method === 'sftp') && (
                  <>
                    <Grid item xs={8}>
                      <TextField
                        margin="dense"
                        label="SSH User"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={serverFormData.ssh_user}
                        onChange={(e) => setServerFormData({ ...serverFormData, ssh_user: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        margin="dense"
                        label="SSH Port"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={serverFormData.ssh_port}
                        onChange={(e) => setServerFormData({ ...serverFormData, ssh_port: parseInt(e.target.value) })}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        margin="dense"
                        label="SSH Password"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={serverFormData.ssh_password}
                        onChange={(e) => setServerFormData({ ...serverFormData, ssh_password: e.target.value })}
                        helperText="Leave empty to use key-based authentication"
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            )}
            
            {/* Webhooks Tab */}
            {tabValue === 3 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="API Key"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={serverFormData.api_key}
                    onChange={(e) => setServerFormData({ ...serverFormData, api_key: e.target.value })}
                    helperText="API key for webhook authentication (auto-generated)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Webhook URLs for Vicidial Configuration
                  </Typography>
                  <TextField
                    fullWidth
                    label="Call Complete Webhook"
                    value={`${window.location.origin}/webhook/vicidial/call-complete`}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton 
                            onClick={() => copyToClipboard(`${window.location.origin}/webhook/vicidial/call-complete`)}
                            size="small"
                          >
                            <CopyIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Recording Complete Webhook"
                    value={`${window.location.origin}/webhook/vicidial/recording-complete`}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton 
                            onClick={() => copyToClipboard(`${window.location.origin}/webhook/vicidial/recording-complete`)}
                            size="small"
                          >
                            <CopyIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Lead Submission Webhook (Full VICIdial Data)"
                    value={`${window.location.origin}/webhook/vicidial/lead-submit`}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton 
                            onClick={() => copyToClipboard(`${window.location.origin}/webhook/vicidial/lead-submit`)}
                            size="small"
                          >
                            <CopyIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ mb: 2 }}
                  />
                  
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <AlertTitle>VICIdial Configuration Notes</AlertTitle>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Lead Submission Webhook:</strong> Use this URL to send comprehensive lead data from VICIdial for service provider integration and lead supplier monitoring.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Example VICIdial URL format:
                    </Typography>
                    <Typography variant="body2" component="pre" sx={{ 
                      backgroundColor: '#f5f5f5', 
                      p: 1, 
                      borderRadius: 1, 
                      fontSize: '0.75rem',
                      overflow: 'auto'
                    }}>
{`${window.location.origin}/webhook/vicidial/lead-submit?lead_id=--A--lead_id--B--&list_id=--A--list_id--B--&vendor_lead_code=--A--vendor_lead_code--B--&campaign=--A--campaign--B--&dispo=--A--dispo--B--&fullname=--A--fullname--B--&title=--A--title--B--&first_name=--A--first_name--B--&last_name=--A--last_name--B--&middle_initial=--A--middle_initial--B--&address1=--A--address1--B--&address2=--A--address2--B--&address3=--A--address3--B--&email=--A--email--B--&dialed_number=--A--dialed_number--B--&phone_number=--A--phone_number--B--&city=--A--city--B--&security_phrase=--A--security_phrase--B--&postal_code=--A--postal_code--B--&county=--A--province--B--&comments=--A--comments--B--&date_of_birth=--A--date_of_birth--B--&alt_phone=--A--alt_phone--B--&called_count=--A--called_count--B--&custom_fields=Y&server_id=YOUR_SERVER_ID&tenant_id=YOUR_TENANT_ID`}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Benefits:</strong> Full customer data capture, lead supplier tracking via List ID, comprehensive service provider integration.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseServerDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {selectedServer ? 'Update' : 'Create'} Server
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Tenants; 